import { Switch, TextField, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { CAP_PERIODS, ICap } from "cap/model"
import { IFormConfigurationCaps } from "form/model"
import { getEmptyCap } from "form/tools/cap"
import { Field, FieldProps, FormikErrors } from "formik"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"

interface IFieldCapsProps {
  disabled?: boolean
  caps: IFormConfigurationCaps
  capErrors: FormikErrors<IFormConfigurationCaps> | undefined
  sx?: any
}

interface IFieldCapProps {
  disabled?: boolean
  error?: string
  period: CAP_PERIODS
  cap: ICap | null
  sx?: any
  onChange: (period: CAP_PERIODS, cap: ICap | null) => void
}

const FIELD_NAME: string = "caps"

const FieldCap = React.memo(({ disabled, period, cap, error, sx, onChange }: IFieldCapProps) => {
  const isDisabled = disabled || cap === null
  return (
    <FlexBox jc="start" fullWidth gap wrap>
      <FlexBox gap>
        <Switch
          checked={cap !== null}
          value={cap !== null}
          onChange={(_event, checked) => onChange(period, checked ? getEmptyCap(period) : null)}
          disabled={disabled}
        />
        <TextField
          value={cap ? cap.frequency : ""}
          size="small"
          error={typeof error === "string" && error !== ""}
          disabled={isDisabled}
          sx={{ maxWidth: "6rem" }}
          type="number"
          onChange={event =>
            onChange(period, {
              ...(cap as ICap),
              frequency: event.target.value !== "" ? Number(event.target.value) : 0
            })
          }
        />
        <Typography color={isDisabled ? "textSecondary" : error ? "error" : "textPrimary"} fontSize=".9rem">
          {translate(`cap.label.${period}`)}
        </Typography>
      </FlexBox>
      {error && !isDisabled && (
        <Typography color="error" fontSize=".9rem" sx={{ textAlign: "end", fontStyle: "italic", fontWeight: 500 }}>
          {error}
        </Typography>
      )}
    </FlexBox>
  )
})

export const FieldCaps = React.memo(({ disabled, caps, sx, capErrors }: IFieldCapsProps) => (
  <Field>
    {({ form: { setFieldValue } }: FieldProps) => {
      const onChange: (period: CAP_PERIODS, cap: ICap | null) => void = (period: CAP_PERIODS, cap: ICap | null) => {
        setFieldValue(FIELD_NAME, {
          ...caps,
          [period]: cap
        })
      }
      return (
        <FlexBox vertical ai="start" fullWidth gap>
          <Typography color="textPrimary" sx={{ fontSize: ".9rem" }}>
            {translate("label.cap.title")}
          </Typography>
          <FieldCap
            period={CAP_PERIODS.MONTH}
            cap={caps.month}
            disabled={disabled}
            error={capErrors?.month}
            onChange={(period: CAP_PERIODS, cap: ICap | null) => onChange(period, cap)}
          />
          <FieldCap
            period={CAP_PERIODS.WEEK}
            cap={caps.week}
            disabled={disabled}
            error={capErrors?.week}
            onChange={(period: CAP_PERIODS, cap: ICap | null) => onChange(period, cap)}
          />
          <FieldCap
            period={CAP_PERIODS.DAY}
            cap={caps.day}
            disabled={disabled}
            error={capErrors?.day}
            onChange={(period: CAP_PERIODS, cap: ICap | null) => onChange(period, cap)}
          />
          <FieldCap
            period={CAP_PERIODS.HOUR}
            cap={caps.hour}
            disabled={disabled}
            error={capErrors?.hour}
            onChange={(period: CAP_PERIODS, cap: ICap | null) => onChange(period, cap)}
          />
          <FieldCap
            period={CAP_PERIODS.MINUTE}
            cap={caps.minute}
            disabled={disabled}
            error={capErrors?.minute}
            onChange={(period: CAP_PERIODS, cap: ICap | null) => onChange(period, cap)}
          />
        </FlexBox>
      )
    }}
  </Field>
))
